// Cruise Command · Canonical Brand Assets Module (Phase 1 · 2026-07-23)
// ---------------------------------------------------------------------
//
// SINGLE SOURCE OF TRUTH for every Cruise Command brand asset URL and
// palette constant referenced from React. Any component that renders
// the logo, wordmark, icon, or favicon MUST import from this module
// rather than embedding string paths. A future logo refresh should
// require exactly one commit — this file.
//
// The canonical shipped artwork lives under `/app/frontend/public/brand/`
// (see the deployment README there). Those files are served statically
// by the frontend host and are also wired into `public/index.html`
// and `public/manifest.json` — those two files reference the same
// URLs directly because they run *before* the React bundle mounts.
//
// This module was created under Brand Package Adoption Phase 1
// (Product Owner authorization 2026-07-23). See
// `/app/docs/brand/PHASE-1-ADOPTION-AUDIT.md` for the full audit and
// the rules governing future changes.

/**
 * Full-wordmark logo variants (the "CRUISE COMMAND" text lockup).
 *
 * IMPORTANT: The "-dark" variant renders WHITE ON DARK BACKGROUNDS
 * (its filename means "for use on dark surfaces", not "dark coloured
 * logo"). See `/frontend/public/brand/README.md` for the naming
 * convention. Consumers should always pick the variant that MATCHES
 * the background:
 *
 *   Light surface → BRAND_LOGO.primary   (navy wordmark + gold accent)
 *   Dark surface  → BRAND_LOGO.reverse   (white wordmark + gold accent)
 */
export const BRAND_LOGO = Object.freeze({
  primary: "/brand/logo/logo.svg",         // light backgrounds
  reverse: "/brand/logo/logo-dark.svg",    // dark backgrounds (white wordmark + gold accent)
  white:   "/brand/logo/logo-white.svg",   // mono knockout — drops gold accent
  black:   "/brand/logo/logo-black.svg",   // mono knockout — solid black
  blue:    "/brand/logo/logo-blue.svg",    // single-colour print — navy
  gold:    "/brand/logo/logo-gold.svg",    // single-colour print — gold
});

/**
 * Standalone icon / compass mark (no wordmark).
 * Used for app-shell icons, PWA install icons, and native platform
 * home-screen icons. The raster sizes below cover iOS + Android +
 * desktop PWA. SVG is preferred everywhere it renders correctly.
 */
export const BRAND_ICON = Object.freeze({
  primary: "/brand/icon/icon.svg",
  dark:    "/brand/icon/icon-dark.svg",
  white:   "/brand/icon/icon-white.svg",
  mark:    "/brand/icon/mark.svg",         // C-mark only, no compass wedge
  markGold:  "/brand/icon/mark-gold.svg",
  markDark:  "/brand/icon/mark-dark.svg",
  markWhite: "/brand/icon/mark-white.svg",
  // Common raster sizes — index.html and manifest.json reference these
  // directly. Kept here so React code can also render an <img> at the
  // canonical size without hard-coding URLs.
  png48:   "/brand/icon/icon-48.png",
  png96:   "/brand/icon/icon-96.png",
  png180:  "/brand/icon/icon-180.png",     // Apple touch icon
  png192:  "/brand/icon/icon-192.png",
  png512:  "/brand/icon/icon-512.png",
  png1024: "/brand/icon/icon-1024.png",
});

/**
 * Favicon (browser tab, small metadata contexts).
 */
export const BRAND_FAVICON = Object.freeze({
  svg16:     "/brand/favicon/favicon-16.svg",
  darkSvg16: "/brand/favicon/favicon-dark-16.svg",
  whiteSvg16:"/brand/favicon/favicon-white-16.svg",
  png16:     "/brand/favicon/favicon-16.png",
  png32:     "/brand/favicon/favicon-32.png",
});

/**
 * Approved brand palette (canonical hex values from the Brand Package,
 * confirmed against the deployed `/brand/README.md`).
 *
 * These are provided for JavaScript/JSX consumption (inline styles,
 * SVG fills, Framer-Motion colour transitions). CSS should continue
 * to use the `--cc-*` custom properties declared in `index.css`. When
 * the two must agree, `index.css` is authoritative for the CSS layer
 * and this module is authoritative for the JS layer.
 *
 * Do NOT introduce a third brand colour without Product Owner sign-off
 * (see `docs/brand/PHASE-1-ADOPTION-AUDIT.md`).
 */
export const BRAND_PALETTE = Object.freeze({
  deepNavy: "#071E3E",    // primary dark — wordmark on light surfaces, theme colour
  warmGold: "#C28E42",    // accent — compass wedge, secondary emphasis
});

/**
 * Product identity constants (kept alongside the visual identity so
 * every branding surface has one place to find copy + colour + art).
 */
export const BRAND_META = Object.freeze({
  productName: "Cruise Command",
  tagline: "Voyage Intelligence",
  themeColor: BRAND_PALETTE.deepNavy,
});

const BrandAssets = Object.freeze({
  logo: BRAND_LOGO,
  icon: BRAND_ICON,
  favicon: BRAND_FAVICON,
  palette: BRAND_PALETTE,
  meta: BRAND_META,
});

export default BrandAssets;
